import React from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "reactstrap";
import { Trash2 } from "react-feather";

const colorConfig = {
  danger:  { circleBg: "#fee2e2", iconColor: "#dc2626" },
  warning: { circleBg: "#fef3c7", iconColor: "#d97706" },
  success: { circleBg: "#dcfce7", iconColor: "#16a34a" },
  primary: { circleBg: "#dbeafe", iconColor: "#2563eb" },
};

/**
 * Reusable confirmation dialog for destructive or sensitive actions.
 *
 * Props:
 *   isOpen        — boolean
 *   onClose       — () => void  (also accepts onCancel as alias)
 *   onConfirm     — () => void
 *   deleting      — boolean     (also accepts loading as alias)
 *
 *   confirmColor  — "danger" | "warning" | "success" | "primary"  (default: "danger")
 *                   Controls button color and icon circle color.
 *
 *   headerText    — string   modal header title         (default: "Confirm Delete")
 *   icon          — react-feather component             (default: Trash2)
 *   title         — string   bold heading in body       (default: "Confirm Delete")
 *   subtitle      — string   muted line under title     (default: "This action cannot be undone")
 *
 *   itemName      — string   used in default body text
 *   bodyText      — node     custom body content; overrides default when provided
 *
 *   warningText   — string   shown in alert box below body
 *   warningLabel  — string   label of the alert box     (default: "Warning")
 *
 *   actionText    — string   confirm button label       (default: "Delete")
 *   loadingText   — string   confirm button label while loading
 */
const ConfirmDeleteDialog = ({
  isOpen,
  onClose,
  onCancel,
  onConfirm,
  deleting = false,
  loading,

  confirmColor = "danger",

  headerText = "Confirm Delete",
  icon: Icon = Trash2,
  title = "Confirm Delete",
  subtitle = "This action cannot be undone",

  itemName,
  bodyText,

  warningText,
  warningLabel = "Warning",

  actionText = "Delete",
  loadingText,
}) => {
  const isLoading = loading !== undefined ? loading : deleting;
  const handleClose = onClose || onCancel;
  const confirmLabel = loadingText || `${actionText}ing...`;
  const { circleBg, iconColor } = colorConfig[confirmColor] || colorConfig.danger;

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="md">
      <ModalHeader toggle={handleClose}>{headerText}</ModalHeader>
      <ModalBody>
        <div className="d-flex align-items-center mb-3">
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              backgroundColor: circleBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: 16,
              flexShrink: 0,
            }}
          >
            <Icon size={24} style={{ color: iconColor }} />
          </div>
          <div>
            <h6 className="mb-1">{title}</h6>
            <p className="mb-0 text-muted" style={{ fontSize: "0.9rem" }}>
              {subtitle}
            </p>
          </div>
        </div>

        <p className="mb-2">
          {bodyText !== undefined ? (
            bodyText
          ) : (
            <>
              Are you sure you want to delete{" "}
              {itemName ? <strong>"{itemName}"</strong> : "this item"}?
            </>
          )}
        </p>

        {warningText && (
          <div
            className="alert alert-warning mb-0"
            style={{ fontSize: "0.85rem", padding: "8px 12px" }}
          >
            <strong>{warningLabel}:</strong> {warningText}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button color={confirmColor} onClick={onConfirm} disabled={isLoading}>
          {isLoading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" />
              {confirmLabel}
            </>
          ) : (
            <>
              <Icon size={14} className="me-1" />
              {actionText}
            </>
          )}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ConfirmDeleteDialog;
